import { drmUpdateTableData } from "../../service/drmUpdateTableData";
import {
  setUpdateDrmTableDataSuccess,
  setUpdateDrmTableDataFailure,
} from "../reducers/drmUpdateTableDataSlice";

import { put, takeLatest, call } from "redux-saga/effects";
import { convertToYYYYDDMM } from "../../helpers";

const sanitizeValue = (value) => (value !== "-" ? value : null);

const mapLineHaulRateFields = (row) => {
  const sanitizedFields = {
    baf: sanitizeValue(row?.baf),
    civa: sanitizeValue(row?.civa),
    ets: sanitizeValue(row?.ets),
  };

  const lineHaulRate = sanitizeValue(row?.lineHaulRate);

  if (!lineHaulRate) {
    return sanitizedFields;
  }

  switch (row?.lineHaulChargeType) {
    case "BAF":
      return { ...sanitizedFields, baf: lineHaulRate };
    case "CIVA":
      return { ...sanitizedFields, civa: lineHaulRate };
    case "ETS":
      return { ...sanitizedFields, ets: lineHaulRate };
    default:
      return { ...sanitizedFields, lineHaulRate };
  }
};

const sanitizeCommonFields = (row) => ({
  carrierScac: sanitizeValue(row?.carrierScac),
  serviceScac: sanitizeValue(row?.serviceScac),
  equipmentType: sanitizeValue(row?.equipmentType),
  region: sanitizeValue(row?.region),
  carrierManager: sanitizeValue(row?.carrierManager),
  laneDescription: sanitizeValue(row?.laneDescription),
  originCity: sanitizeValue(row?.originCity),
  originState: sanitizeValue(row?.originState),
  originCountry: sanitizeValue(row?.originCountry),
  originZone: sanitizeValue(row?.originZone),
  originZoneShortDesc: sanitizeValue(row?.originZoneShortDesc),
  originPostalCode: sanitizeValue(row?.originPostalCode),
  destinationCity: sanitizeValue(row?.destinationCity),
  destinationState: sanitizeValue(row?.destinationState),
  destinationPostalCode: sanitizeValue(row?.destinationPostalCode),
  destinationCountry: sanitizeValue(row?.destinationCountry),
  destinationZone: sanitizeValue(row?.destinationZone),
  destinationZoneShortDesc: sanitizeValue(row?.destinationZoneShortDesc),
  primaryCustomers: sanitizeValue(row?.primaryCustomers),
  lineHaulChargeType: sanitizeValue(row?.lineHaulChargeType),
  effectiveDate: sanitizeValue(row?.effectiveDate),
  expirationDate: sanitizeValue(row?.expirationDate),
  awardBackup: sanitizeValue(row?.awardBackup),
  qtyActiveFlag: sanitizeValue(row?.qtyActiveFlag),
  minShipmentsAllocated: sanitizeValue(row?.minShipmentsAllocated),
  maxShipmentsAllocated: sanitizeValue(row?.maxShipmentsAllocated),
  percentageActiveFlag: sanitizeValue(row?.percentageActiveFlag),
  minCarrierAllocationPercentage: sanitizeValue(row?.minCarrierAllocationPercentage),
  maxCarrierAllocationPercentage: sanitizeValue(row?.maxCarrierAllocationPercentage),
  modeOfTransport: sanitizeValue(row?.modeOfTransport),
  status: typeof row?.status === 'number' ? row.status : 0,
  message: sanitizeValue(row?.message),
  errors: Array.isArray(row?.errors) ? row.errors : [],
  versionNumber: sanitizeValue(row?.versionNumber),
  sagaId: sanitizeValue(row?.sagaId),
  id: sanitizeValue(row?.id),
  defaultError: sanitizeValue(row?.defaultError),
});

const sanitizeBidSpecificFields = (row) => ({
  itemType: sanitizeValue(row?.itemType),
  item: sanitizeValue(row?.item),
  estimatedMileagePcMiler: sanitizeValue(row?.estimatedMileagePcMiler),
  annualShipmentVolume: sanitizeValue(row?.annualShipmentVolume),
  availableAnnualShipmentVolume: sanitizeValue(row?.availableAnnualShipmentVolume),
  annualMaxShipmentVolume: sanitizeValue(row?.annualMaxShipmentVolume),
  bidRateWoFuel: sanitizeValue(row?.bidRateWoFuel),
  laneServiceType: sanitizeValue(row?.laneServiceType),
  incoterm: sanitizeValue(row?.incoterm),
  supplier: sanitizeValue(row?.supplier),
  transitTime: sanitizeValue(row?.transitTime),
  originPortCountry: sanitizeValue(row?.originPortCountry),
  originPort: sanitizeValue(row?.originPort),
  destinationTransportationMode: sanitizeValue(row?.destinationTransportationMode),
  tradeLane: sanitizeValue(row?.tradeLane),
  destinationPort: sanitizeValue(row?.destinationPort),
  destinationPortCountry: sanitizeValue(row?.destinationPortCountry),
  bucketType: sanitizeValue(row?.bucketType),
  ...mapLineHaulRateFields(row),
  changeComment: sanitizeValue(row?.changeComment),
  coupaDetails: row?.coupaFile && typeof row.coupaFile === 'object' ? row.coupaFile : {},
  coupaFileRecordErrors: Array.isArray(row?.coupaFileRecordErrors) ? row.coupaFileRecordErrors : [],
});

const sanitizeDailySpecificFields = (row) => ({
  rangeCode: sanitizeValue(row?.rangeCode),
  rangeToUnits: sanitizeValue(row?.rangeToUnits),
  rate: sanitizeValue(row?.rate),
  qtyBucketType: sanitizeValue(row?.qtyBucketType),
  pctBucketType: sanitizeValue(row?.pctBucketType),
  qtyStartDate: convertToYYYYDDMM(sanitizeValue(row?.effectiveDate)),
  qtyEndDate: convertToYYYYDDMM(sanitizeValue(row?.expirationDate)),
  pctStartDate: convertToYYYYDDMM(sanitizeValue(row?.effectiveDate)),
  pctEndDate: convertToYYYYDDMM(sanitizeValue(row?.expirationDate)),
  miles: sanitizeValue(row?.miles),
  orderType: sanitizeValue(row?.orderType),
  preAwarded: sanitizeValue(row?.preAwarded),
  btfRate: sanitizeValue(row?.btfRate),
  destHub: sanitizeValue(row?.destHub),
  orgHub: sanitizeValue(row?.orgHub),
  changeComment: sanitizeValue(row?.changeComment),
  carrierName: sanitizeValue(row?.carrierName),
  systemRateId: sanitizeValue(row?.systemRateId),
  tariffDetails: sanitizeValue(row?.tariffDetails),
});

const sanitizeRowData = (row, tableName) => {
  const commonFields = sanitizeCommonFields(row);

  if (tableName === "drmBidTableData") {
    return {
      ...row,
      ...commonFields,
      ...sanitizeBidSpecificFields(row),
    };
  } else if (tableName === "drmDailyTableData") {
    return {
      ...row,
      ...commonFields,
      ...sanitizeDailySpecificFields(row),
    };
  }

  return {
    ...row,
    ...commonFields,
  };
};

const formatTablePayload = (details, tableName, userCode) => {
  if (tableName === "drmBidTableData") {
    return {
      userCode,
      coupaFileId: null,
      bidDetails: details,
    };
  } else if (tableName === "drmDailyTableData") {
    return {
      // userCode,
      data: details,
    };
  }
  return {};
};

export function* workerUpdateDrmTableDat(action) {
  const { payload } = action;

  const details = payload.updatedTableData.map((row) => 
    sanitizeRowData(row, payload.tableName)
  );

  const updatedTableDataList = formatTablePayload(details, payload.tableName, payload.userCode);

  try {
    const tableName = payload.tableName;
    const response = yield call(drmUpdateTableData, [
      updatedTableDataList,
      tableName,
      payload.controller,
    ]);

    if (response?.status === 200) {
      yield put(setUpdateDrmTableDataSuccess(response.data.message));
    } else {
      yield put(setUpdateDrmTableDataFailure(response.status));
    }
  } catch (error) {
    yield put(setUpdateDrmTableDataFailure(error.toString()));
  }
}

function* drmUpdateTableDataSaga() {
  yield takeLatest("updateDrmTable/setUpdateDrmTableData", workerUpdateDrmTableDat);
}

export default drmUpdateTableDataSaga;